// Informes programados: contrato de /reports/scheduled y lo decidible del
// formulario (validación, topes, destinatarios, textos de frecuencia). Puro y
// con tests (scheduled.test.ts); la página sólo pinta.
// Backend: apps/torre-de-control-api/functions/src/modules/scheduledReports/.

// quick = Informes rápidos; full = los reportes de la sección Reportes que se
// generan con GET /reports (report = su typeReport).
export type ReportKind = "quick" | "full";
export type QuickReportId = "fleet" | "mileage" | "stops" | "overspeed" | "alerts" | "bitacora";
export type FullReportId =
  | "historic"
  | "routes"
  | "stops"
  | "mileage"
  | "odometer"
  | "speed"
  | "driving"
  | "geofences"
  | "events"
  | "temperature"
  | "commands"
  | "trailers";
export type ScheduledReportId = QuickReportId | FullReportId;
export type Frequency = "daily" | "weekly" | "monthly";
export type TempScale = "celsius" | "fahrenheit";
// Los mismos nombres que cada sección manda a GET /reports. Sólo viajan los
// del tipo elegido (FULL_PARAM_KEYS).
export type FullParams = {
  minSpeed?: number;
  maxSpeed?: number;
  rpm?: number;
  tempScale?: TempScale;
  highLimit?: number;
  lowLimit?: number;
  geofences?: string[];
};
export type FileFormat = "xlsx" | "pdf";
// skipped_empty: el periodo no tuvo datos y no se mandó correo (regla en tables.js del API).
export type RunStatus = "queued" | "running" | "ok" | "error" | "skipped_empty";

export type ScheduleInput = {
  name: string;
  kind?: ReportKind; // ausente = quick (las programaciones de antes)
  report: ScheduledReportId;
  params?: FullParams; // sólo con kind "full"
  device_ids: string[]; // vacío = todas las unidades (quick salvo la bitácora); full: obligatorio
  frequency: Frequency;
  weekday: number | null; // 1 = lunes … 7 = domingo
  day_of_month: number | null; // 1-28
  time: string; // HH:mm
  tz: string;
  formats: FileFormat[];
  recipients: string[];
  active: boolean;
};

export type ScheduledReport = ScheduleInput & {
  id: string;
  company_id: string;
  created_by: string;
  next_run_at: number | null; // null = pausada
  last_run_at: number | null;
  last_status: RunStatus | null;
  last_status_at: number | null;
  last_error: string | null;
  last_period?: { from: string; to: string } | null;
};

// Los mismos nombres e íconos que el catálogo de Informes rápidos.
export const SCHEDULABLE: { id: QuickReportId; name: string; icon: string }[] = [
  { id: "fleet", name: "Resumen de flota", icon: "ri-dashboard-3-line" },
  { id: "mileage", name: "Kilometraje", icon: "ri-road-map-line" },
  { id: "stops", name: "Paradas", icon: "ri-stop-circle-line" },
  { id: "overspeed", name: "Excesos de velocidad", icon: "ri-speed-up-line" },
  { id: "alerts", name: "Alertas", icon: "ri-alarm-warning-line" },
  { id: "bitacora", name: "Bitácora de jornada", icon: "ri-steering-2-line" },
];
// Los mismos nombres, íconos y orden que el menú de Reportes (ReportLayout).
// dailySummary no está: no tiene sección en v5 (lo consume la app móvil).
export const SCHEDULABLE_FULL: { id: FullReportId; name: string; icon: string }[] = [
  { id: "historic", name: "Historial", icon: "ri-history-line" },
  { id: "routes", name: "Viajes", icon: "ri-route-line" },
  { id: "stops", name: "Paradas", icon: "ri-stop-circle-line" },
  { id: "mileage", name: "Kilometraje", icon: "ri-road-map-line" },
  { id: "odometer", name: "Odómetro", icon: "ri-dashboard-3-line" },
  { id: "speed", name: "Velocidad", icon: "ri-speed-up-line" },
  { id: "driving", name: "Conducción", icon: "ri-steering-2-line" },
  { id: "geofences", name: "Geocercas", icon: "ri-map-pin-range-line" },
  { id: "events", name: "Eventos", icon: "ri-calendar-event-line" },
  { id: "temperature", name: "Temperatura", icon: "ri-thermometer-line" },
  { id: "commands", name: "Comandos", icon: "ri-terminal-line" },
  { id: "trailers", name: "Remolques", icon: "ri-truck-line" },
];

// mileage y stops existen en los dos catálogos: el tipo decide cuál es.
export const reportMeta = (id: string, kind: ReportKind = "quick") =>
  (kind === "full" ? SCHEDULABLE_FULL : SCHEDULABLE).find((r) => r.id === id) ?? { id, name: id, icon: "ri-file-list-3-line" };

// ---------- Reportes completos: topes y parámetros ----------

// historic y events salen punto por punto: con más unidades o un mes entero el
// archivo se vuelve inmanejable, así que el backend los acota.
const HEAVY: FullReportId[] = ["historic", "events"];
export const maxUnits = (report: string) => (HEAVY.includes(report as FullReportId) ? 20 : 50);
export const allowedFrequencies = (kind: ReportKind, report: string): Frequency[] =>
  kind === "full" && HEAVY.includes(report as FullReportId) ? ["daily", "weekly"] : ["daily", "weekly", "monthly"];

export const FULL_PARAM_KEYS: Partial<Record<FullReportId, (keyof FullParams)[]>> = {
  speed: ["minSpeed"],
  driving: ["maxSpeed", "rpm"],
  temperature: ["tempScale", "highLimit", "lowLimit"],
  geofences: ["geofences"],
};

// Lo que se edita en el formulario: los números como texto (para poder
// escribir "-18" o "2.5" sin que el input los reescriba) y todos los
// parámetros a la vez, así cambiar de tipo y volver no pierde lo escrito.
export type ParamsDraft = {
  minSpeed: string;
  maxSpeed: string;
  rpm: string;
  tempScale: TempScale;
  highLimit: string;
  lowLimit: string;
  geofences: string[];
};
export type ScheduleForm = Omit<ScheduleInput, "kind" | "params"> & { kind: ReportKind; params: ParamsDraft };

// Los mismos defaults que aplica GET /reports cuando el campo de la sección
// llega vacío (reportsController.js): así un programado sin tocar sale igual.
export const DEFAULT_PARAMS: ParamsDraft = {
  minSpeed: "80",
  maxSpeed: "120",
  rpm: "2500",
  tempScale: "celsius",
  highLimit: "11",
  lowLimit: "2",
  geofences: [],
};

const draftFrom = (p: FullParams | undefined): ParamsDraft => {
  const num = (v: number | undefined, d: string) => (typeof v === "number" && Number.isFinite(v) ? String(v) : d);
  return {
    minSpeed: num(p?.minSpeed, DEFAULT_PARAMS.minSpeed),
    maxSpeed: num(p?.maxSpeed, DEFAULT_PARAMS.maxSpeed),
    rpm: num(p?.rpm, DEFAULT_PARAMS.rpm),
    tempScale: p?.tempScale === "fahrenheit" ? "fahrenheit" : "celsius",
    highLimit: num(p?.highLimit, DEFAULT_PARAMS.highLimit),
    lowLimit: num(p?.lowLimit, DEFAULT_PARAMS.lowLimit),
    geofences: Array.isArray(p?.geofences) ? p.geofences : [],
  };
};

const toNum = (s: string) => (s.trim() === "" ? NaN : Number(s));

const FREQ_NAME: Record<Frequency, string> = { daily: "diario", weekly: "semanal", monthly: "mensual" };

/**
 * Cambia el informe del formulario y corrige lo que deja de valer con el
 * nuevo: unidades de más, la bitácora a una sola, mensual en historic/events.
 * `note` explica lo que se corrigió para mostrarlo; null si no hubo nada.
 */
export const switchReport = (f: ScheduleForm, kind: ReportKind, report: ScheduledReportId): { form: ScheduleForm; note: string | null } => {
  const next: ScheduleForm = { ...f, kind, report };
  const notes: string[] = [];
  const name = reportMeta(report, kind).name;
  const cap = kind === "full" ? maxUnits(report) : kind === "quick" && report === "bitacora" ? 1 : Infinity;
  if (next.device_ids.length > cap) {
    next.device_ids = next.device_ids.slice(0, cap);
    notes.push(cap === 1 ? "La bitácora es de una sola unidad: dejé la primera." : `${name} admite hasta ${cap} unidades: dejé las primeras ${cap}.`);
  }
  const allowed = allowedFrequencies(kind, report);
  if (!allowed.includes(next.frequency)) {
    const to = allowed[allowed.length - 1];
    notes.push(`${name} no se programa ${FREQ_NAME[next.frequency]}: lo cambié a ${FREQ_NAME[to]}.`);
    next.frequency = to;
  }
  return { form: next, note: notes.length ? notes.join(" ") : null };
};

export const WEEKDAYS = ["lunes", "martes", "miércoles", "jueves", "viernes", "sábado", "domingo"];
export const MAX_RECIPIENTS = 20;
// La misma expresión que valida el backend (scheduledReportsSchema.js).
const EMAIL_RE = /^[^\s@,;<>"]+@[^\s@,;<>"]+\.[^\s@,;<>"]{2,}$/;
export const isEmail = (s: string) => EMAIL_RE.test(s);

// Lo que llega en STALE_RUN_MS del backend: un envío "en cola" más viejo que
// esto ya murió y se puede volver a pedir.
const STALE_RUN_MS = 15 * 60 * 1000;
export const isBusy = (s: Pick<ScheduledReport, "last_status" | "last_status_at">, now = Date.now()) =>
  (s.last_status === "queued" || s.last_status === "running") && now - (s.last_status_at ?? 0) < STALE_RUN_MS;

export const emptySchedule = (tz: string): ScheduleForm => ({
  name: "",
  kind: "quick",
  report: "fleet",
  params: { ...DEFAULT_PARAMS },
  device_ids: [],
  frequency: "daily",
  weekday: 1,
  day_of_month: 1,
  time: "07:00",
  tz,
  formats: ["xlsx"],
  recipients: [],
  active: true,
});

export const toInput = (s: ScheduledReport): ScheduleForm => ({
  name: s.name,
  kind: s.kind ?? "quick",
  report: s.report,
  params: draftFrom(s.params),
  device_ids: s.device_ids ?? [],
  frequency: s.frequency,
  weekday: s.weekday ?? 1,
  day_of_month: s.day_of_month ?? 1,
  time: s.time,
  tz: s.tz,
  formats: s.formats,
  recipients: s.recipients,
  active: s.active,
});

// ---------- Destinatarios ----------

/**
 * Agrega lo escrito o pegado (separado por comas, punto y coma, espacios o
 * saltos de línea) a la lista: minúsculas, sin repetidos y hasta 20. Lo que no
 * es un correo se devuelve en `invalid` para dejarlo en el campo.
 */
export const addRecipients = (current: string[], text: string) => {
  const list = [...current];
  const invalid: string[] = [];
  let overflow = 0;
  for (const raw of text.split(/[\s,;]+/)) {
    const email = raw.trim().replace(/^<|>$/g, "").toLowerCase();
    if (!email) continue;
    if (!isEmail(email)) invalid.push(raw.trim());
    else if (list.includes(email)) continue;
    else if (list.length >= MAX_RECIPIENTS) overflow++;
    else list.push(email);
  }
  return { recipients: list, invalid, overflow };
};

// ---------- Validación ----------

export type FormErrors = Partial<
  Record<
    | "name"
    | "device_ids"
    | "frequency"
    | "weekday"
    | "day_of_month"
    | "time"
    | "formats"
    | "recipients"
    | "minSpeed"
    | "maxSpeed"
    | "rpm"
    | "limits"
    | "geofences",
    string
  >
>;

const positive = (s: string) => Number.isFinite(toNum(s)) && toNum(s) > 0;

const validateParams = (report: ScheduledReportId, p: ParamsDraft, e: FormErrors) => {
  if (report === "speed" && !positive(p.minSpeed)) e.minSpeed = "Escribe el límite en km/h.";
  if (report === "driving") {
    if (!positive(p.maxSpeed)) e.maxSpeed = "Escribe la velocidad en km/h.";
    if (!positive(p.rpm)) e.rpm = "Escribe las revoluciones.";
  }
  if (report === "temperature") {
    const lo = toNum(p.lowLimit);
    const hi = toNum(p.highLimit);
    if (!Number.isFinite(lo) || !Number.isFinite(hi)) e.limits = "Escribe los dos límites.";
    else if (lo >= hi) e.limits = "El límite bajo tiene que ser menor que el alto.";
  }
  if (report === "geofences" && !p.geofences.length) e.geofences = "Elige al menos una geocerca.";
};

export const validateSchedule = (f: ScheduleForm): FormErrors => {
  const e: FormErrors = {};
  if (!f.name.trim()) e.name = "Ponle un nombre.";
  else if (f.name.trim().length > 80) e.name = "Máximo 80 caracteres.";
  if (f.kind === "full") {
    const cap = maxUnits(f.report);
    if (!f.device_ids.length) e.device_ids = "Elige al menos una unidad.";
    else if (f.device_ids.length > cap) e.device_ids = `Máximo ${cap} unidades en este reporte.`;
    if (!allowedFrequencies(f.kind, f.report).includes(f.frequency)) e.frequency = "Este reporte sólo se programa diario o semanal.";
    validateParams(f.report, f.params, e);
  } else if (f.report === "bitacora" && f.device_ids.length !== 1) e.device_ids = "La bitácora es de una sola unidad.";
  if (f.frequency === "weekly" && !(Number.isInteger(f.weekday) && f.weekday! >= 1 && f.weekday! <= 7)) e.weekday = "Elige el día.";
  if (f.frequency === "monthly" && !(Number.isInteger(f.day_of_month) && f.day_of_month! >= 1 && f.day_of_month! <= 28))
    e.day_of_month = "Del 1 al 28.";
  if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(f.time)) e.time = "Hora inválida.";
  if (!f.formats.length) e.formats = "Elige al menos un formato.";
  if (!f.recipients.length) e.recipients = "Agrega al menos un correo.";
  else if (f.recipients.length > MAX_RECIPIENTS) e.recipients = `Máximo ${MAX_RECIPIENTS} correos.`;
  else if (!f.recipients.every(isEmail)) e.recipients = "Hay un correo inválido.";
  return e;
};

// Lo que viaja al API: sin los campos que no aplican a la frecuencia ni al
// tipo. Un informe rápido sale sin kind ni params, igual que antes; uno
// completo, sólo con los params de su tipo y en número.
export const toPayload = (f: ScheduleForm): ScheduleInput => {
  const { kind, params: draft, ...rest } = f;
  const base: ScheduleInput = {
    ...rest,
    name: f.name.trim(),
    weekday: f.frequency === "weekly" ? f.weekday : null,
    day_of_month: f.frequency === "monthly" ? f.day_of_month : null,
  };
  if (kind !== "full") return base;
  const keys = FULL_PARAM_KEYS[f.report as FullReportId] ?? [];
  if (!keys.length) return { ...base, kind };
  const params: FullParams = {};
  for (const k of keys) {
    if (k === "tempScale") params.tempScale = draft.tempScale;
    else if (k === "geofences") params.geofences = draft.geofences;
    else params[k] = toNum(draft[k]);
  }
  return { ...base, kind, params };
};

// ---------- Textos ----------

export const frequencyText = (s: Pick<ScheduleInput, "frequency" | "weekday" | "day_of_month" | "time">) => {
  if (s.frequency === "weekly") return `Cada ${WEEKDAYS[(s.weekday ?? 1) - 1]} a las ${s.time}`;
  if (s.frequency === "monthly") return `El día ${s.day_of_month ?? 1} de cada mes a las ${s.time}`;
  return `Todos los días a las ${s.time}`;
};

// Qué días cubre cada envío (schedule.periodFor en el backend).
export const PERIOD_TEXT: Record<Frequency, string> = {
  daily: "el día anterior",
  weekly: "los 7 días anteriores",
  monthly: "el mes anterior completo",
};

export const unitsText = (ids: string[], names: Map<string, string>) =>
  !ids.length ? "Todas las unidades" : ids.length === 1 ? names.get(ids[0]) ?? "1 unidad" : `${ids.length} unidades`;
